import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FormationItemData } from '../../../shared/dto/EntityRepository';
import { InputWork } from '../../../shared/component/headless/input/InputBody';
import { TEXTBLOCK_CAPTION_MAX_LENGTH } from '../../../shared/constants/limits';
import { getPageMessage } from '../../../shared/constants/pageMessages';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import { useSettingStore } from '../../../shared/store/settingStore';
import {
  formatFormationItemInfo,
  getFormationItemSource,
  resolveFormationSlotImage,
} from '../../../shared/util/formationUtils';
import { shouldShowWorkArtist } from '../../../shared/util/workArtist';
import {
  FORMATION_INFO_GAP,
  FORMATION_INFO_ROW_GAP,
} from './formationGrid';

interface FormationInfoStackProps {
  peId: string;
  items: FormationItemData[];
  isEditMode: boolean;
  offsetY?: number;
  rightInsetRatio?: number;
  rightInsetPx?: number;
}

const FormationInfoStack: React.FC<FormationInfoStackProps> = ({
  peId,
  items,
  isEditMode,
  offsetY = 0,
  rightInsetRatio = 0,
  rightInsetPx,
}) => {
  const updateFormationSlotCaption = useProjectElementListStore(
    (state) => state.updateFormationSlotCaption,
  );
  const setting = useSettingStore((state) => state.setting);
  const includeArtist = shouldShowWorkArtist(setting);
  const populatedItems = useMemo(
    () => items.filter((item) => resolveFormationSlotImage(item) !== null),
    [items],
  );

  if (populatedItems.length === 0) return null;

  return (
    <InfoStack
      $offsetY={offsetY}
      $rightInset={rightInsetPx === undefined
        ? `${rightInsetRatio * 100}%`
        : `${rightInsetPx}px`}
    >
      {populatedItems.map((item) => {
        const source = getFormationItemSource(item);
        const info = formatFormationItemInfo(item, includeArtist) || '—';
        const isCaptionEditable = isEditMode && source === 'directUpload';

        return (
          <InfoRow key={item.id}>
            <Index>{item.slotIndex + 1}.</Index>
            {isCaptionEditable ? (
              <CaptionInput
                type="text"
                value={item.caption ?? ''}
                placeholder={getPageMessage('DETAIL_DESCRIPTION_PLACEHOLDER')}
                maxLength={TEXTBLOCK_CAPTION_MAX_LENGTH}
                aria-label={`Image ${item.slotIndex + 1} information`}
                onChange={(event) => updateFormationSlotCaption(
                  peId,
                  item.slotIndex,
                  event.currentTarget.value,
                )}
              />
            ) : (
              <Info title={info}>{info}</Info>
            )}
          </InfoRow>
        );
      })}
    </InfoStack>
  );
};

const InfoStack = styled.div<{ $offsetY: number; $rightInset: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  margin-top: ${({ $offsetY }) => `${FORMATION_INFO_GAP + $offsetY}px`};
  padding-right: ${({ $rightInset }) => $rightInset};
  gap: ${FORMATION_INFO_ROW_GAP}px;
  transition:
    margin-top 0.35s ease-in-out,
    padding-right 0.35s ease-in-out;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  gap: 4px;
  text-align: right;
  white-space: nowrap;
`;

const Index = styled.span`
  flex: 0 0 auto;
`;

const Info = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CaptionInput = styled(InputWork)`
  flex: 0 1 320px;
  min-width: 0;
  width: min(50cqw, 320px);
  max-width: calc(100cqw - 24px);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default FormationInfoStack;
